import React from "react";
import { observer } from "mobx-react-lite";
import { useTranslation } from "react-i18next";
import DashboardSimpleNumber from "features/Dashboards/Base/DashboardSimpleNumber";
import Badge from "components/Badge";
import { GWP_M2_AREA_UNIT } from "utils";
import { levelsDashboardStore } from "../LevelsDashboardStore";
import NotAvailableChip from "./NotAvailableChip";

function SubIndicator(props: {
  label: React.ReactNode;
  value: number | null | undefined;
  note?: string;
}) {
  return (
    <div className="flex flex-col items-center text-center gap-2">
      <span className="text-md text-gray-500">{props.label}</span>
      {props.value != null ? (
        <DashboardSimpleNumber value={props.value} unit={GWP_M2_AREA_UNIT} />
      ) : (
        <NotAvailableChip />
      )}
      {props.note && (
        <span className="text-[10px] text-gray-400">{props.note}</span>
      )}
    </div>
  );
}

/**
 * GWP sub-indicators sourced from the hotspot endpoint, so construction-scope
 * only (B6 excluded) — flagged by the chip in the title.
 */
export default observer(function GwpSubIndicators() {
  const { t } = useTranslation();
  const totals = levelsDashboardStore.gwpIndicatorTotals;

  return (
    <div className="rounded-lg bg-gray-100 p-5 mb-5">
      <div className="flex items-center gap-2 mb-3">
        <h3 className="text-xs font-bold uppercase text-gray-400">
          {t("levels.gwpSubIndicators")}
        </h3>
        <Badge type="attention" size="small">
          {t("levels.withoutB6")}
        </Badge>
      </div>
      <div className="grid grid-cols-4 gap-4">
        <SubIndicator
          label={t("levels.gwpTotal")}
          value={totals["match_calculations.gwp_total"]}
        />
        <SubIndicator
          label={t("levels.gwpFossil")}
          value={totals["match_calculations.gwpfossil_total"]}
        />
        <SubIndicator
          label={t("levels.gwpBiogen")}
          value={totals["match_calculations.gwpbiogenic_total"]}
          note={t("levels.gwpBiogenNote")}
        />
        <SubIndicator
          label={
            <>
              {t("levels.gwpLuluc")}{" "}
              <span className="text-gray-400">({t("levels.optional")})</span>
            </>
          }
          value={totals["match_calculations.gwpluluc_total"]}
          note={t("levels.gwpLulucNote")}
        />
      </div>
    </div>
  );
});
